import { useEffect, useRef } from 'react';
import { siteConfig } from '../lib/config';

export interface SyncEventPayload {
  accountId: string;
  userId: string;
  syncType: string[];
  skipped?: boolean;
  reason?: string;
  success?: boolean;
  at: number;
}

/**
 * Lắng nghe SSE stream `/api/v1/sync/events` từ server.
 *
 * Server subscribe Redis channel mà sync worker publish lên mỗi khi sync
 * xong 1 account, forward event về đây. Khi nhận event, gọi `onEvent`
 * (thường là fetch lại account list để cập nhật số dư / betlog mới nhất).
 *
 * - Tự reconnect khi connection bị đóng (EventSource native behavior + backoff).
 * - Dừng lắng nghe khi unmount.
 * - Auth qua cookie httpOnly (access_token) — gửi kèm nhờ withCredentials: true.
 *
 * @param onEvent callback khi nhận event sync-completed
 * @param enabled nếu false thì không mở connection
 */
export function useSyncEvents(onEvent: (payload: SyncEventPayload) => void, enabled = true): void {
  const cbRef = useRef(onEvent);
  cbRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return;

    let es: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let closed = false;
    let retryDelay = 1000;

    const connect = () => {
      if (closed) return;
      const url = `${siteConfig.apiUrl}/api/v1/sync/events`;
      es = new EventSource(url, { withCredentials: true });

      es.addEventListener('sync-completed', (e: MessageEvent) => {
        try {
          const payload = JSON.parse(e.data) as SyncEventPayload;
          cbRef.current(payload);
        } catch {
          // ignore malformed
        }
      });

      es.onopen = () => {
        retryDelay = 1000; // reset backoff khi kết nối lại thành công
      };

      es.onerror = () => {
        es?.close();
        es = null;
        if (closed) return;
        // Backoff exponential: 1s → 2s → 4s → ... tối đa 30s
        retryTimer = setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 2, 30_000);
      };
    };

    connect();

    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      es?.close();
      es = null;
    };
  }, [enabled]);
}